/**
 * AnnotationLayer - Figma-style comment pins overlay on PDF pages
 * Supports @ mentions, drag-to-reposition, 50% opacity until hover
 */

import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, Calculator, Send, Trash2 } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import { useOptionalCRM } from '../../utils/CRMContext';
import { shouldLoadEmployeeMentionDirectory } from '../../utils/employeeMentionDirectory';
import { useEmployeeMentionDirectory } from '../../utils/useEmployeeMentionDirectory';
import { canAppearInMentionAutocomplete, isInternalCommentMentionUser } from '../../utils/mentionAffiliation';
import { isArchitectDecisionAudience, mentionUsersForCommentAudience, type CommentAudience } from '../../utils/commentAudience';
import { isArchitectRole } from '../../utils/userRoles';
import CommentAudienceControl from '../pm/CommentAudienceControl';
import { ArchitectRoleBadge, DecisionForPill, WorkingNotesPill } from '../pm/ArchitectRoleBadge';
import '../pm/ArchitectRoleBadge.css';
import UserHoverCard from '../ui/UserHoverCard';
import { syncCommentComposerTextareaHeight } from '../../utils/commentComposerTextarea';
import { applyPlainCommentListKeydown } from '../../utils/richCommentListShortcuts';
import type { PdfAnnotation, CommentMention } from '../../types';
import UserAvatar from '../ui/UserAvatar';
import { AnnotationMentionDropdown } from './AnnotationMentionDropdown';
import {
  updateAnnotationPosition,
  deleteAnnotation,
  markAnnotationAsRead,
  pdfAnnotationHasUnreadForUser,
} from '../../utils/pdfAnnotationService';
import { preprocessSlackMrkdwnUserMentionsInPlainBody, PLAIN_COMMENT_MENTION_SPLIT_RE } from '../../utils/richComment';
import { mentionsNotifySomeone, nextUntaggedSendGate, type UntaggedSendSource } from '../../utils/untaggedCommentConfirm';
import { resolveTextMentionUserIds } from '../../utils/pdfAnnotationMentionUtils';
import UntaggedSendHint from '../pm/UntaggedSendHint';
import './AnnotationLayer.css';

interface AnnotationLayerProps {
  pageNumber: number;
  scale: number;
  annotations: PdfAnnotation[];
  loading: boolean;
  /** When false, canvas clicks do not add comments (draw/measure modes). */
  interactive?: boolean;
  /** View existing pins only — no new comments, replies, drag, or delete. */
  readOnly?: boolean;
  pinModeActive?: boolean;
  onAddAnnotation: (pageNumber: number, x: number, y: number, content: string, mentions?: CommentMention[]) => Promise<void>;
  onAddReply: (annotationId: string, content: string, mentions?: CommentMention[]) => Promise<void>;
  onDeleteAnnotation?: () => void;
  onAnnotationMoved?: () => void;
  currentUserId?: string;
  /** When set, auto-expand this annotation (e.g. when opening from comment feed) */
  focusAnnotationId?: string;
  /** Called when parent-driven focus changes or user expands a pin */
  onFocusAnnotation?: (annotationId: string) => void;
  /** Called when user expands an annotation (for marking @ mentions as read) */
  onAnnotationViewed?: (annotationId: string) => void;
  /** Override staff persistence (portal viewer passes portal-scoped handlers). */
  onMoveAnnotation?: (annotationId: string, x: number, y: number) => Promise<void>;
  onDeleteAnnotationById?: (annotationId: string) => Promise<void>;
  /** When set, controls which pins show the delete control (defaults to author-only). */
  canDeleteAnnotation?: (annotation: PdfAnnotation) => boolean;
  architectSurface?: boolean;
  pinAudience?: CommentAudience;
  onPinAudienceChange?: (next: CommentAudience) => void;
  architectName?: string;
  hasAssignedArchitect?: boolean;
  /** Hiring resumes, purchasing attachments, and portal files still notify people with no @mention. */
  notifiesWithoutMention?: boolean;
}

const getFirstName = (displayName: string): string => displayName.split(' ')[0] || displayName;

type UntaggedTarget = { kind: 'new' } | { kind: 'reply'; id: string };

function sameUntaggedTarget(a: UntaggedTarget | null, b: UntaggedTarget): boolean {
  if (!a) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'reply' && b.kind === 'reply') return a.id === b.id;
  return true;
}

const formatAnnotationTime = (value: Date | string | number | undefined): string => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatDistanceToNow(date, { addSuffix: true });
};

export function AnnotationLayer({
  pageNumber,
  scale,
  annotations,
  loading,
  interactive = true,
  readOnly = false,
  pinModeActive = false,
  onAddAnnotation,
  onAddReply,
  onDeleteAnnotation,
  onAnnotationMoved,
  currentUserId,
  focusAnnotationId,
  onFocusAnnotation,
  onAnnotationViewed,
  onMoveAnnotation,
  onDeleteAnnotationById,
  canDeleteAnnotation,
  architectSurface = false,
  pinAudience = 'internal',
  onPinAudienceChange,
  architectName = 'the architect',
  hasAssignedArchitect = false,
  notifiesWithoutMention = false,
}: AnnotationLayerProps) {
  const crm = useOptionalCRM();
  const { user } = useAuth();
  const mentionFallback = useEmployeeMentionDirectory(
    !crm?.crmUsers?.length && shouldLoadEmployeeMentionDirectory(user?.role),
  );
  const crmUsers = crm?.crmUsers?.length ? crm.crmUsers : mentionFallback;
  const viewerRole = user?.role;
  const [addingAt, setAddingAt] = useState<{ x: number; y: number } | null>(null);
  const [newComment, setNewComment] = useState('');
  const [pendingMentions, setPendingMentions] = useState<CommentMention[]>([]);
  const [untaggedTarget, setUntaggedTarget] = useState<UntaggedTarget | null>(null);
  const untaggedTargetRef = useRef<UntaggedTarget | null>(null);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(focusAnnotationId ?? null);
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [replyPendingMentions, setReplyPendingMentions] = useState<Record<string, CommentMention[]>>({});
  const [replyMentionForId, setReplyMentionForId] = useState<string | null>(null);
  const [replyMentionSearch, setReplyMentionSearch] = useState('');
  const [replyMentionStartIndex, setReplyMentionStartIndex] = useState(-1);
  const [replyShowMentionDropdown, setReplyShowMentionDropdown] = useState(false);
  const [replySelectedMentionIndex, setReplySelectedMentionIndex] = useState(0);
  const replyInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const dragOffsetRef = useRef({ dx: 0, dy: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bubbleScrollRef = useRef<HTMLDivElement | null>(null);
  /** When true, live annotation updates keep the thread scrolled to the latest reply. */
  const bubblePinnedToBottomRef = useRef(true);
  const prevExpandedIdRef = useRef<string | null>(null);
  /** Parent focus last applied to expandedId — avoids re-opening on annotation subscription updates. */
  const lastAppliedFocusRef = useRef<string | undefined>(undefined);
  /** Last focus+user pair that triggered mark-as-read (so a late user id still syncs). */
  const lastMarkedReadRef = useRef<string | undefined>(undefined);

  const scrollBubbleToBottom = useCallback(() => {
    const el = bubbleScrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, []);

  const handleBubbleScroll = useCallback(() => {
    const el = bubbleScrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    bubblePinnedToBottomRef.current = distanceFromBottom < 8;
  }, []);
  // Auto-expand when parent focus changes (deep link, comments list). Skip when only annotations
  // refresh so clicking another pin is not overridden by the first opened comment.
  useEffect(() => {
    if (!focusAnnotationId) {
      lastAppliedFocusRef.current = undefined;
      lastMarkedReadRef.current = undefined;
      return;
    }
    if (!annotations.some(a => a.id === focusAnnotationId)) return;
    if (lastAppliedFocusRef.current !== focusAnnotationId) {
      lastAppliedFocusRef.current = focusAnnotationId;
      setExpandedId(focusAnnotationId);
      onFocusAnnotation?.(focusAnnotationId);
    }
    if (currentUserId) {
      const markKey = `${focusAnnotationId}:${currentUserId}`;
      if (lastMarkedReadRef.current !== markKey) {
        lastMarkedReadRef.current = markKey;
        void markAnnotationAsRead(focusAnnotationId, currentUserId);
        onAnnotationViewed?.(focusAnnotationId);
      }
    }
  }, [focusAnnotationId, annotations, currentUserId, onFocusAnnotation, onAnnotationViewed]);

  useEffect(() => {
    if (!expandedId) {
      prevExpandedIdRef.current = null;
      return;
    }
    if (expandedId !== prevExpandedIdRef.current) {
      prevExpandedIdRef.current = expandedId;
      bubblePinnedToBottomRef.current = true;
      scrollBubbleToBottom();
    }
  }, [expandedId, scrollBubbleToBottom]);

  useEffect(() => {
    if (!expandedId || !bubblePinnedToBottomRef.current) return;
    scrollBubbleToBottom();
  }, [annotations, expandedId, scrollBubbleToBottom]);

  useLayoutEffect(() => {
    if (!expandedId) return;
    syncCommentComposerTextareaHeight(replyInputRef.current);
  }, [expandedId, replyText]);

  useEffect(() => {
    const onResize = () => syncCommentComposerTextareaHeight(replyInputRef.current);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const photoForUid = (uid: string) => crmUsers.find((u) => u.uid === uid)?.photoURL;

  const mentionableUsers = mentionUsersForCommentAudience(
    crmUsers.filter((u) =>
      canAppearInMentionAutocomplete(u, viewerRole) && isInternalCommentMentionUser(u),
    ),
    architectSurface ? 'architect' : pinAudience,
    architectSurface || isArchitectRole(user?.role),
  );
  const filteredMentionUsers = mentionableUsers.filter(
    u =>
      (u.displayName || '').toLowerCase().includes(mentionSearch.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(mentionSearch.toLowerCase())
  );
  const filteredReplyMentionUsers = mentionableUsers.filter(
    u =>
      (u.displayName || '').toLowerCase().includes(replyMentionSearch.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(replyMentionSearch.toLowerCase())
  );

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!interactive || readOnly || loading || pinModeActive || draggingId) return;
      // If a comment bubble is expanded, close it when clicking elsewhere
      if (expandedId) {
        setExpandedId(null);
        return;
      }
      if (addingAt) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setAddingAt({ x, y });
      setNewComment('');
      setPendingMentions([]);
    },
    [interactive, readOnly, loading, addingAt, pinModeActive, draggingId, expandedId]
  );

  const handleCommentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      const cursorPos = e.target.selectionStart || 0;
      setNewComment(value);
      if (untaggedTargetRef.current?.kind === 'new') {
        untaggedTargetRef.current = null;
        setUntaggedTarget(null);
      }
      const textBeforeCursor = value.substring(0, cursorPos);
      const atIndex = textBeforeCursor.lastIndexOf('@');
      if (atIndex !== -1) {
        const textAfterAt = textBeforeCursor.substring(atIndex + 1);
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          setMentionSearch(textAfterAt);
          setMentionStartIndex(atIndex);
          setShowMentionDropdown(true);
          setSelectedMentionIndex(0);
          return;
        }
      }
      setShowMentionDropdown(false);
    },
    []
  );

  const insertMention = useCallback(
    (mentionUser: { uid: string; displayName: string }) => {
      if (mentionStartIndex === -1) return;
      const firstName = getFirstName(mentionUser.displayName || '');
      const displayMention = `@${firstName} `;
      const before = newComment.substring(0, mentionStartIndex);
      const after = newComment.substring(mentionStartIndex + mentionSearch.length + 1);
      const newMention: CommentMention = {
        userId: mentionUser.uid,
        userName: firstName,
        startIndex: mentionStartIndex,
        endIndex: mentionStartIndex + displayMention.length - 1,
      };
      setPendingMentions(prev => [...prev, newMention]);
      setNewComment(before + displayMention + after);
      if (untaggedTargetRef.current?.kind === 'new') {
        untaggedTargetRef.current = null;
        setUntaggedTarget(null);
      }
      setShowMentionDropdown(false);
      setMentionSearch('');
      setMentionStartIndex(-1);
      setTimeout(() => textareaRef.current?.focus(), 0);
    },
    [mentionStartIndex, mentionSearch, newComment]
  );

  const handleAddKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (showMentionDropdown && filteredMentionUsers.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedMentionIndex(prev => (prev < filteredMentionUsers.length - 1 ? prev + 1 : 0));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedMentionIndex(prev => (prev > 0 ? prev - 1 : filteredMentionUsers.length - 1));
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          insertMention(filteredMentionUsers[selectedMentionIndex]);
          return;
        }
        if (e.key === 'Escape') {
          setShowMentionDropdown(false);
          return;
        }
      }
      applyPlainCommentListKeydown(e, e.currentTarget, setNewComment, 'enter');
    },
    [showMentionDropdown, filteredMentionUsers, selectedMentionIndex, insertMention]
  );

  const fileCommentIsTagged = useCallback(
    (text: string, mentions?: CommentMention[]) => {
      if (mentionsNotifySomeone(mentions, user?.id)) return true;
      const ids = resolveTextMentionUserIds(
        text,
        mentionableUsers.map((u) => ({ uid: u.uid, displayName: u.displayName || '' })),
      );
      return ids.some((id) => id && id !== user?.id);
    },
    [mentionableUsers, user?.id],
  );

  const allowFileSend = useCallback(
    (target: UntaggedTarget, text: string, mentions: CommentMention[] | undefined, source: UntaggedSendSource) => {
      const next = nextUntaggedSendGate({
        tagged: fileCommentIsTagged(text, mentions),
        source,
        awaitingClick: sameUntaggedTarget(untaggedTargetRef.current, target),
        skip: notifiesWithoutMention,
      });
      const nextTarget = next.awaitingClick ? target : null;
      untaggedTargetRef.current = nextTarget;
      setUntaggedTarget(nextTarget);
      return next.action === 'send';
    },
    [fileCommentIsTagged, notifiesWithoutMention],
  );

  const dismissUntagged = useCallback(() => {
    untaggedTargetRef.current = null;
    setUntaggedTarget(null);
  }, []);

  const handleSubmitNew = useCallback(async () => {
    if (!addingAt || !newComment.trim()) return;
    if (!allowFileSend({ kind: 'new' }, newComment, pendingMentions, 'click')) return;
    setSubmitting(true);
    try {
      await onAddAnnotation(pageNumber, addingAt.x, addingAt.y, newComment.trim(), pendingMentions.length ? pendingMentions : undefined);
      setAddingAt(null);
      setNewComment('');
      setPendingMentions([]);
    } finally {
      setSubmitting(false);
    }
  }, [addingAt, allowFileSend, newComment, pendingMentions, pageNumber, onAddAnnotation]);

  const handleCancelAdd = useCallback(() => {
    setAddingAt(null);
    setNewComment('');
    setPendingMentions([]);
    if (untaggedTargetRef.current?.kind === 'new') {
      untaggedTargetRef.current = null;
      setUntaggedTarget(null);
    }
  }, []);

  const handleAddReply = useCallback(
    async (annotationId: string, source: UntaggedSendSource = 'click') => {
      const text = replyText[annotationId];
      if (!text?.trim()) return;
      const mentions = replyPendingMentions[annotationId];
      if (!allowFileSend({ kind: 'reply', id: annotationId }, text, mentions, source)) return;
      setSubmitting(true);
      try {
        bubblePinnedToBottomRef.current = true;
        await onAddReply(annotationId, text.trim(), mentions?.length ? mentions : undefined);
        setReplyText(prev => ({ ...prev, [annotationId]: '' }));
        setReplyPendingMentions(prev => {
          const next = { ...prev };
          delete next[annotationId];
          return next;
        });
      } finally {
        setSubmitting(false);
      }
    },
    [allowFileSend, replyText, replyPendingMentions, onAddReply]
  );

  const handleReplyChange = useCallback(
    (annotationId: string, value: string, cursorPos?: number) => {
      setReplyText(prev => ({ ...prev, [annotationId]: value }));
      if (untaggedTargetRef.current?.kind === 'reply' && untaggedTargetRef.current.id === annotationId) {
        untaggedTargetRef.current = null;
        setUntaggedTarget(null);
      }
      const pos = cursorPos ?? value.length;
      const textBeforeCursor = value.substring(0, pos);
      const atIndex = textBeforeCursor.lastIndexOf('@');
      if (atIndex !== -1) {
        const textAfterAt = textBeforeCursor.substring(atIndex + 1);
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          setReplyMentionForId(annotationId);
          setReplyMentionSearch(textAfterAt);
          setReplyMentionStartIndex(atIndex);
          setReplyShowMentionDropdown(true);
          setReplySelectedMentionIndex(0);
          return;
        }
      }
      setReplyShowMentionDropdown(false);
    },
    []
  );

  const insertReplyMention = useCallback(
    (annotationId: string, mentionUser: { uid: string; displayName: string }) => {
      if (replyMentionStartIndex === -1) return;
      const text = replyText[annotationId] || '';
      const firstName = getFirstName(mentionUser.displayName || '');
      const displayMention = `@${firstName} `;
      const before = text.substring(0, replyMentionStartIndex);
      const after = text.substring(replyMentionStartIndex + replyMentionSearch.length + 1);
      const newMention: CommentMention = {
        userId: mentionUser.uid,
        userName: firstName,
        startIndex: replyMentionStartIndex,
        endIndex: replyMentionStartIndex + displayMention.length - 1,
      };
      setReplyPendingMentions(prev => ({
        ...prev,
        [annotationId]: [...(prev[annotationId] || []), newMention],
      }));
      setReplyText(prev => ({ ...prev, [annotationId]: before + displayMention + after }));
      if (untaggedTargetRef.current?.kind === 'reply' && untaggedTargetRef.current.id === annotationId) {
        untaggedTargetRef.current = null;
        setUntaggedTarget(null);
      }
      setReplyShowMentionDropdown(false);
      setReplyMentionSearch('');
      setReplyMentionStartIndex(-1);
      setReplyMentionForId(null);
      setTimeout(() => replyInputRef.current?.focus(), 0);
    },
    [replyMentionStartIndex, replyMentionSearch, replyText]
  );

  const handleReplyKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>, annotationId: string) => {
      if (replyShowMentionDropdown && replyMentionForId === annotationId && filteredReplyMentionUsers.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setReplySelectedMentionIndex(prev => (prev < filteredReplyMentionUsers.length - 1 ? prev + 1 : 0));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setReplySelectedMentionIndex(prev => (prev > 0 ? prev - 1 : filteredReplyMentionUsers.length - 1));
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const idx = Math.min(replySelectedMentionIndex, filteredReplyMentionUsers.length - 1);
          insertReplyMention(annotationId, filteredReplyMentionUsers[idx]);
          return;
        }
        if (e.key === 'Escape') {
          setReplyShowMentionDropdown(false);
          return;
        }
      }
      if (
        applyPlainCommentListKeydown(e, e.currentTarget, (value) => {
          setReplyText((prev) => ({ ...prev, [annotationId]: value }));
        }, 'enter')
      ) {
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleAddReply(annotationId, 'keyboard');
      }
    },
    [replyShowMentionDropdown, replyMentionForId, filteredReplyMentionUsers, replySelectedMentionIndex, insertReplyMention, handleAddReply]
  );

  const dragStartRef = useRef<{ startX: number; startY: number; annX: number; annY: number } | null>(null);

  const handlePinMouseDown = useCallback(
    (e: React.MouseEvent, ann: PdfAnnotation) => {
      if (readOnly || !ann.id || e.button !== 0) return;
      // Allow text selection / textarea interaction inside the expanded bubble.
      const target = e.target as Element | null;
      if (target?.closest?.('.annotation-bubble')) return;
      e.stopPropagation();
      setDraggingId(ann.id);
      setDragOffset({ dx: 0, dy: 0 });
      dragOffsetRef.current = { dx: 0, dy: 0 };
      dragStartRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        annX: ann.x,
        annY: ann.y,
      };
    },
    [readOnly]
  );

  // Reset reply selection when filter text changes so first match is selected
  useEffect(() => {
    if (replyShowMentionDropdown) {
      setReplySelectedMentionIndex(0);
    }
  }, [replyMentionSearch]);

  // Close expanded comment when clicking outside the annotation layer (e.g. timber panel)
  useEffect(() => {
    if (!expandedId) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const target = e.target as Node | null;
      if (target instanceof Element && target.closest('[data-annotation-mention-menu]')) return;
      if (!containerRef.current.contains(target as Node)) {
        setExpandedId(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [expandedId]);

  useEffect(() => {
    if (!draggingId || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const onMove = (e: MouseEvent) => {
      const start = dragStartRef.current;
      if (!start) return;
      const dx = ((e.clientX - start.startX) / rect.width) * 100;
      const dy = ((e.clientY - start.startY) / rect.height) * 100;
      dragOffsetRef.current = { dx, dy };
      setDragOffset({ dx, dy });
    };
    const onUp = async () => {
      const start = dragStartRef.current;
      const offset = dragOffsetRef.current;
      if (start && draggingId) {
        const newX = Math.max(0, Math.min(100, start.annX + offset.dx));
        const newY = Math.max(0, Math.min(100, start.annY + offset.dy));
        if (newX !== start.annX || newY !== start.annY) {
          try {
            if (onMoveAnnotation) {
              await onMoveAnnotation(draggingId, newX, newY);
            } else {
              await updateAnnotationPosition(draggingId, newX, newY);
            }
            onAnnotationMoved?.();
          } catch (err) {
            console.error('Error moving annotation:', err);
          }
        }
      }
      setDraggingId(null);
      setDragOffset({ dx: 0, dy: 0 });
      dragStartRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [draggingId, onAnnotationMoved, onMoveAnnotation]);

  const renderContent = (content: string) => {
    const raw = preprocessSlackMrkdwnUserMentionsInPlainBody(content);
    return raw.split(PLAIN_COMMENT_MENTION_SPLIT_RE).map((part, i) =>
      part.startsWith('@') ? (
        <span key={i} className="annotation-mention">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div
      ref={containerRef}
      className={`annotation-layer${interactive || readOnly ? ' annotation-layer--interactive' : ''}`}
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      data-comment-hint={interactive && !readOnly && !pinModeActive && annotations.length === 0 ? 'true' : undefined}
      onClick={interactive && !readOnly ? handleCanvasClick : undefined}
    >
      {annotations.map(ann => {
        const isDragging = draggingId === ann.id;
        const displayX = ann.x + (isDragging ? dragOffset.dx : 0);
        const displayY = ann.y + (isDragging ? dragOffset.dy : 0);
        const isAgent = ann.source === 'biddingAgent' || ann.source === 'marketingAgent';
        return (
          <div
            key={ann.id}
            className={`annotation-pin ${isDragging ? 'dragging' : ''} ${expandedId === ann.id ? 'expanded' : ''} ${isAgent ? 'annotation-pin--agent' : ''}`}
            style={{
              left: `${displayX}%`,
              top: `${displayY}%`,
              transform: 'translate(-50%, -50%)',
              pointerEvents: interactive || readOnly ? 'auto' : 'none',
            }}
            onMouseDown={e => handlePinMouseDown(e, ann)}
            onClick={e => {
              e.stopPropagation();
              if (!isDragging) {
                const willExpand = expandedId !== ann.id;
                setExpandedId(prev => (prev === ann.id ? null : ann.id || null));
                if (willExpand && ann.id) {
                  onFocusAnnotation?.(ann.id);
                  if (currentUserId) {
                    void markAnnotationAsRead(ann.id, currentUserId);
                    onAnnotationViewed?.(ann.id);
                  }
                }
              }
            }}
          >
            {currentUserId && pdfAnnotationHasUnreadForUser(ann, currentUserId) && (
              <span className="annotation-pin-unread" aria-label="Unread" />
            )}
            {ann.replies && ann.replies.length > 0 && expandedId !== ann.id && (
              <span className="annotation-pin-reply-badge" aria-label={`${ann.replies.length} replies`}>
                {ann.replies.length}
              </span>
            )}
            {isAgent ? <Calculator size={16} /> : <MessageSquare size={16} />}
            {expandedId === ann.id && (
              <div
                className={[
                  'annotation-bubble',
                  displayX > 62 ? 'annotation-bubble--left' : '',
                  displayY < 20 ? 'annotation-bubble--anchor-top' : '',
                  displayY > 80 ? 'annotation-bubble--anchor-bottom' : '',
                ].filter(Boolean).join(' ')}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
              >
                <div
                  className="annotation-bubble-scroll"
                  ref={el => {
                    if (expandedId === ann.id) bubbleScrollRef.current = el;
                  }}
                  onScroll={expandedId === ann.id ? handleBubbleScroll : undefined}
                >
                  <div className="annotation-thread">
                    <div className="annotation-comment">
                      <UserHoverCard
                        className={`annotation-avatar${isArchitectRole(crmUsers.find((u) => u.uid === ann.userId)?.role) ? ' tc-avatar--architect' : ''}`}
                        name={ann.userName}
                        userId={ann.userId}
                        photoURL={photoForUid(ann.userId)}
                        crmUser={crmUsers.find((u) => u.uid === ann.userId)}
                      />
                      <div className="annotation-comment-body">
                        <div className="annotation-comment-meta">
                          <span className="annotation-author">{ann.userName}</span>
                          {isArchitectRole(crmUsers.find((u) => u.uid === ann.userId)?.role) && <ArchitectRoleBadge compact />}
                          {!architectSurface && (
                            isArchitectDecisionAudience(ann.audience)
                              ? <DecisionForPill name={architectName} />
                              : <WorkingNotesPill />
                          )}
                          <span className="annotation-date">{formatAnnotationTime(ann.createdAt)}</span>
                          {ann.replies && ann.replies.length > 0 && (
                            <span className="annotation-reply-count">
                              {ann.replies.length} {ann.replies.length === 1 ? 'reply' : 'replies'}
                            </span>
                          )}
                          {!readOnly && onDeleteAnnotation && currentUserId && (
                            canDeleteAnnotation
                              ? canDeleteAnnotation(ann)
                              : ann.userId === currentUserId
                          ) && (
                            <button
                              type="button"
                              className="annotation-delete-btn"
                              onClick={async e => {
                                e.stopPropagation();
                                if (ann.id && window.confirm('Delete this comment?')) {
                                  try {
                                    if (onDeleteAnnotationById) {
                                      await onDeleteAnnotationById(ann.id);
                                    } else {
                                      await deleteAnnotation(ann.id);
                                    }
                                    onDeleteAnnotation?.();
                                    setExpandedId(null);
                                  } catch (err) {
                                    console.error('Error deleting annotation:', err);
                                  }
                                }
                              }}
                              title="Delete comment"
                              aria-label="Delete comment"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                        <div className="annotation-comment-text">{renderContent(ann.content)}</div>
                      </div>
                    </div>
                    {ann.replies && ann.replies.length > 0 && (
                      <div className="annotation-replies">
                        {ann.replies.map((r, i) => (
                          <div key={i} className="annotation-comment annotation-comment--reply">
                            <UserAvatar
                              className="annotation-avatar"
                              name={r.userName}
                              userId={r.userId}
                              photoURL={photoForUid(r.userId)}
                            />
                            <div className="annotation-comment-body">
                              <div className="annotation-comment-meta">
                                <span className="annotation-author">{r.userName}</span>
                                {r.createdAt && (
                                  <span className="annotation-date">{formatAnnotationTime(r.createdAt)}</span>
                                )}
                              </div>
                              <div className="annotation-comment-text">{renderContent(r.content)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {!readOnly ? (
                <div className="annotation-reply-form">
                  <textarea
                    ref={el => {
                      if (expandedId === ann.id) replyInputRef.current = el;
                    }}
                    rows={2}
                    placeholder="Write a reply… (@ to mention)"
                    value={replyText[ann.id || ''] || ''}
                    onChange={e =>
                      handleReplyChange(ann.id!, e.target.value, e.target.selectionStart ?? undefined)
                    }
                    onKeyDown={e => handleReplyKeyDown(e, ann.id!)}
                  />
                  {replyShowMentionDropdown && replyMentionForId === ann.id && (
                    <AnnotationMentionDropdown
                      anchor={replyInputRef.current}
                      users={filteredReplyMentionUsers.slice(0, 8)}
                      selectedIndex={Math.min(replySelectedMentionIndex, Math.max(filteredReplyMentionUsers.length - 1, 0))}
                      emptyLabel={mentionableUsers.length === 0 ? 'No users to mention' : 'No matches'}
                      onSelect={u => insertReplyMention(ann.id!, u)}
                    />
                  )}
                  <div className="annotation-reply-form-row">
                    <span className="annotation-composer-hint">Enter to send · Shift+Enter for a new line</span>
                    <UntaggedSendHint
                      open={untaggedTarget?.kind === 'reply' && untaggedTarget.id === ann.id}
                      onDismiss={dismissUntagged}
                    >
                    <button
                      type="button"
                      className="annotation-send-btn"
                      onClick={() => handleAddReply(ann.id!)}
                      disabled={submitting || !replyText[ann.id || '']?.trim()}
                      title="Reply"
                      aria-label="Reply"
                    >
                      <Send size={14} />
                    </button>
                    </UntaggedSendHint>
                  </div>
                </div>
                ) : null}
              </div>
            )}
          </div>
        );
      })}

      {addingAt && (
        <div
          className="annotation-add-form"
          style={{
            left: `${addingAt.x}%`,
            top: `${addingAt.y}%`,
            transform: 'translate(-50%, -50%)',
          }}
          onClick={e => e.stopPropagation()}
        >
          {!architectSurface && onPinAudienceChange && (
            <CommentAudienceControl
              audience={pinAudience}
              onChange={onPinAudienceChange}
              architectName={architectName}
              hasAssignedArchitect={hasAssignedArchitect}
            />
          )}
          <textarea
            ref={textareaRef}
            placeholder="Write a comment… (@ to mention)"
            value={newComment}
            onChange={handleCommentChange}
            onKeyDown={handleAddKeyDown}
            autoFocus
            rows={3}
          />
          {showMentionDropdown && (
            <AnnotationMentionDropdown
              anchor={textareaRef.current}
              users={filteredMentionUsers.slice(0, 8)}
              selectedIndex={selectedMentionIndex}
              emptyLabel={mentionableUsers.length === 0 ? 'No users to mention' : 'No matches'}
              onSelect={u => insertMention(u)}
            />
          )}
          <div className="annotation-add-actions">
            <button type="button" onClick={handleCancelAdd}>
              Cancel
            </button>
            <UntaggedSendHint
              open={untaggedTarget?.kind === 'new'}
              onDismiss={dismissUntagged}
            >
            <button
              type="button"
              className="annotation-send-btn"
              onClick={handleSubmitNew}
              disabled={submitting || !newComment.trim()}
              title={submitting ? 'Saving…' : 'Save comment'}
              aria-label={submitting ? 'Saving…' : 'Save comment'}
            >
              <Send size={14} />
            </button>
            </UntaggedSendHint>
          </div>
        </div>
      )}
    </div>
  );
}
